import { useEffect, useState } from "react";
import { MagnifyingGlass, DiscordLogo, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_COLORS = {
  member: "text-emerald-400 border-emerald-500",
  left: "text-red-400 border-red-500",
  never_joined: "text-white/50 border-white/20",
};

export default function MembersTab() {
  const [rows, setRows] = useState([]);
  const [creators, setCreators] = useState([]);
  const [filters, setFilters] = useState({ creator_id: "", q: "", status: "" });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filters.creator_id) params.creator_id = filters.creator_id;
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      const [a, c] = await Promise.all([
        api.get("/attributions", { params }),
        creators.length ? Promise.resolve({ data: creators }) : api.get("/creators"),
      ]);
      setRows(a.data);
      if (!creators.length) setCreators(c.data);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  function copy(txt) { navigator.clipboard.writeText(txt); toast.success("Copied"); }

  const counts = {
    total: rows.length,
    member: rows.filter(r => r.status === "member").length,
    left: rows.filter(r => r.status === "left").length,
    never_joined: rows.filter(r => r.status === "never_joined").length,
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="display-heading font-black text-3xl">Discord Members</h2>
        <div className="kbd-label text-white/40 mt-1">EVERY VERIFIED USER · WHICH CREATOR BROUGHT THEM · CURRENT STATUS</div>
      </div>

      {/* Filter bar */}
      <div className="card-tactical p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="kbd-label mb-1">SEARCH (USERNAME OR DISCORD ID)</div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="members-search"
              placeholder="alpha_user, 123456789…"
              className="w-full bg-black/60 border border-white/10 pl-9 pr-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none"
            />
          </div>
        </div>
        <div>
          <div className="kbd-label mb-1">CREATOR</div>
          <select value={filters.creator_id} onChange={(e) => setFilters({ ...filters, creator_id: e.target.value })} data-testid="members-filter-creator" className="bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm">
            <option value="">All creators</option>
            {creators.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
          </select>
        </div>
        <div>
          <div className="kbd-label mb-1">STATUS</div>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="members-filter-status" className="bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm">
            <option value="">All</option>
            <option value="member">Current members</option>
            <option value="left">Left</option>
            <option value="never_joined">Never joined</option>
          </select>
        </div>
        <button onClick={() => setFilters({ creator_id: "", q: "", status: "" })} className="ghost-btn px-3 py-2 kbd-label text-white">CLEAR</button>
      </div>

      {/* Quick counts */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "TOTAL", value: counts.total, color: "#00F0FF" },
          { label: "CURRENT MEMBER", value: counts.member, color: "#22C55E" },
          { label: "LEFT", value: counts.left, color: "#FF3B30" },
          { label: "VERIFIED, NEVER JOINED", value: counts.never_joined, color: "#A1A1AA" },
        ].map(s => (
          <div key={s.label} className="card-tactical p-4">
            <div className="kbd-label">{s.label}</div>
            <div className="stat-value text-2xl mt-1" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="kbd-label text-left text-white/50 border-b border-white/10">
              <th className="px-6 py-3">DISCORD</th>
              <th>DISCORD ID</th>
              <th>ATTRIBUTED TO</th>
              <th>CAMPAIGN</th>
              <th>FIRST VERIFIED</th>
              <th>JOINED</th>
              <th>LEFT</th>
              <th>STATUS</th>
              <th>SPEND</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map(r => (
              <tr key={r.discord_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`member-row-${r.discord_id}`}>
                <td className="px-6 py-3 inline-flex items-center gap-2">
                  <DiscordLogo size={14} color="#00F0FF" />
                  <span className="font-sans">@{r.username || "unknown"}</span>
                </td>
                <td className="text-white/60">
                  <button onClick={() => copy(r.discord_id)} className="hover:text-neon-cyan inline-flex items-center gap-1" title="Copy Discord ID">
                    {r.discord_id?.slice(0, 12)}… <Copy size={11} />
                  </button>
                </td>
                <td>
                  <div className="font-sans text-white">{r.creator_name || "—"}</div>
                  <div className="text-xs text-white/40">@{r.creator_slug}</div>
                </td>
                <td className="text-neon-yellow">{r.campaign || "default"}</td>
                <td className="text-white/60 text-xs">{r.first_verified_at?.slice(0, 10)}</td>
                <td className="text-white/60 text-xs">{r.first_joined_at?.slice(0, 10) || "—"}</td>
                <td className="text-white/60 text-xs">{r.left_at?.slice(0, 10) || "—"}</td>
                <td>
                  <span className={`kbd-label px-2 py-1 border ${STATUS_COLORS[r.status]}`}>{r.status.replace("_", " ").toUpperCase()}</span>
                </td>
                <td>
                  {r.total_spent > 0 ? (
                    <span className="text-emerald-400">${r.total_spent} <span className="text-white/40 text-xs">({r.purchase_count}×)</span></span>
                  ) : <span className="text-white/30">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="px-6 py-10 text-white/40 text-center">
                No attributed users yet. Once someone completes Discord OAuth from a referral link, they show up here.
              </td></tr>
            )}
            {loading && <tr><td colSpan={9} className="px-6 py-6 text-white/40 text-center kbd-label text-neon-cyan">// LOADING</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
